import React, { useContext } from 'react';
import { useHistory } from 'react-router-dom';

import { showSnackbar } from 'helpers/snackbar';

import { media } from 'context';
import AppContext from 'context/app';
import { Grid, Layout, PillButton } from 'ds';
import { formatUsd } from 'helpers';
import { PANTRY_SUBSCRIPTION_PATH } from 'routes';
import { BulkUpdateConsumablesProductSubscription, ConsumablesProduct } from 'shared/types';
import { actions } from 'store/Consumables';
import { useAppDispatch } from 'store/hooks';

import ConsumablesOnboardingChangesNotSavedModal from './ConsumablesOnboardingChangesNotSavedModal';
import ConsumablesOnboardingRestockingBanner from './ConsumablesOnboardingRestockingBanner';
import ConsumablesProductCard from '../ConsumablesProductCard';
import { setUpProductSubscriptions } from '../requests';
import { EditableConsumablesOnboardingProduct } from '../types';

interface ConsumablesOboardingSetPageMainContentProps {
  handleShowToast: () => void;
  onProductCardClick: (product: ConsumablesProduct) => void;
  setIsChangesNotSavedModalVisible: (isVisible: boolean) => void;
  isChangesNotSavedModalVisible: boolean;
  editableProducts: EditableConsumablesOnboardingProduct[];
  setEditableProducts: (
    products:
      | EditableConsumablesOnboardingProduct[]
      | ((prev: EditableConsumablesOnboardingProduct[]) => EditableConsumablesOnboardingProduct[])
  ) => void;
  onRestockingBannerClick?: () => void;
}

const ConsumablesOboardingSetPageMainContent: React.FC<ConsumablesOboardingSetPageMainContentProps> = ({
  handleShowToast,
  onProductCardClick,
  setIsChangesNotSavedModalVisible,
  isChangesNotSavedModalVisible,
  editableProducts,
  setEditableProducts,
  onRestockingBannerClick
}) => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { isMobile, lg, xl } = useContext(media);
  const { bottomNavigationHeight } = useContext(AppContext);

  const handleAddQuantityClick = (consumablesProduct: ConsumablesProduct, quantity: number) => {
    setEditableProducts(prev => {
      const newProducts = [...prev];
      const productIndex = newProducts.findIndex(p => p.consumables_product.id === consumablesProduct.id);
      newProducts[productIndex].quantity = quantity;
      return newProducts;
    });
  };

  const itemsTotal = editableProducts.reduce((acc, product) => acc + product.quantity, 0);
  const itemsTotalPrice = editableProducts.reduce(
    (acc, product) => acc + product.quantity * product.consumables_product.amount_cents,
    0
  );

  const handleAddToCartClick = async () => {
    try {
      const bulkUpdateConsumablesProductSubscriptions: BulkUpdateConsumablesProductSubscription[] = editableProducts.map(
        product => ({
          consumables_product_id: product.consumables_product.id,
          quantity: product.quantity
        })
      );
      const response = await setUpProductSubscriptions(bulkUpdateConsumablesProductSubscriptions);
      if (response.status === 200) {
        dispatch(actions.setConsumablesProductSubscriptions(response.data.consumables_product_subscriptions));
        history.push(PANTRY_SUBSCRIPTION_PATH);
        handleShowToast();
      } else {
        showSnackbar({
          message: 'Failed to add items to cart. Please refresh the page and try again.',
          negative: true
        });
      }
    } catch (error) {
      showSnackbar({
        message: 'Failed to add items to cart. Please refresh the page and try again.',
        negative: true
      });
    }
  };

  if (editableProducts.length === 0) {
    return null;
  }

  return (
    <Layout
      marginTop={40}
      paddingX={isMobile ? 24 : 80}
      direction="column"
      gap={isMobile ? 64 : 80}
      paddingBottom={120}
      maxWidth={isMobile ? undefined : 1380}
    >
      <Grid itemsPerRow={xl ? 4 : lg ? 3 : 2} gapY={isMobile ? 64 : 80} gapX={24}>
        {editableProducts.slice(0, 8).map((editableProduct, index) => (
          <ConsumablesProductCard
            key={index}
            product={editableProduct.consumables_product}
            onButtonClick={() => {}}
            onProductCardClick={() => onProductCardClick(editableProduct.consumables_product)}
            useAddQuantity
            currentQuantity={editableProduct.quantity}
            onAddQuantityClick={quantity => {
              handleAddQuantityClick(editableProduct.consumables_product, quantity);
            }}
          />
        ))}
      </Grid>
      <ConsumablesOnboardingRestockingBanner
        size={isMobile ? 'mobile' : 'lg'}
        onBannerClick={onRestockingBannerClick || undefined}
      />
      <Grid itemsPerRow={xl ? 4 : lg ? 3 : 2} gapY={isMobile ? 64 : 80} gapX={24}>
        {editableProducts.slice(8).map((editableProduct, index) => (
          <ConsumablesProductCard
            key={index}
            product={editableProduct.consumables_product}
            onButtonClick={() => {}}
            onProductCardClick={() => onProductCardClick(editableProduct.consumables_product)}
            useAddQuantity
            currentQuantity={editableProduct.quantity}
            onAddQuantityClick={quantity => {
              handleAddQuantityClick(editableProduct.consumables_product, quantity);
            }}
          />
        ))}
      </Grid>
      <Layout
        height={80}
        position="fixed"
        bottom={isMobile ? bottomNavigationHeight : 0}
        left={0}
        right={0}
        color={isMobile ? undefined : 'white'}
        paddingY={16}
        paddingX={isMobile ? 24 : 80}
        boxShadow={!isMobile ? '0 8px 24px 0 rgba(0, 0, 0, 0.1200)' : undefined}
        justify="center"
      >
        <Layout width={isMobile ? '100%' : 316}>
          <PillButton
            type="purpleGradient"
            size="lg"
            text={`Add ${itemsTotal} items to cart • ${formatUsd(itemsTotalPrice / 100)}`}
            fullWidth
            onClick={handleAddToCartClick}
          />
        </Layout>
      </Layout>
      <ConsumablesOnboardingChangesNotSavedModal
        isVisible={isChangesNotSavedModalVisible}
        onClose={() => setIsChangesNotSavedModalVisible(false)}
      />
    </Layout>
  );
};

export default ConsumablesOboardingSetPageMainContent;
